// ============================================================================
// LIT-CASE — Accounts section (ADR-0001 §3.1.4)
// ============================================================================
// Loan accounts clubbed into one litigation. The primary account (mirrored
// into lit.tbl_case_account, is_primary=true) shows first as a read-only badge;
// additional accounts can be added (searched from actionable accounts),
// re-designated primary, or removed.
//
// Backend: caseAccountService (getByCase / create / delete / markPrimary).

import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useCaseAccounts } from 'hooks/api/useCaseAccounts';
import { caseAccountService } from 'api/services/caseAccountService';
import { accountCasesService } from 'api/services/accountCasesService';
import type { CaseAccount } from 'api/types/caseAccount.types';
import type { LitigationCase } from 'api/types/litigationCase.types';

interface Props {
  kase: LitigationCase;
  onMutated: () => void;
}

const fmtAmt = (n?: number | null) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

const ROLE_LABEL: Record<string, string> = {
  PRIMARY: 'Primary', ADDITIONAL_CLAIM: 'Additional', DISMISSED: 'Dismissed',
};

const CaseAccountsSection: React.FC<Props> = ({ kase, onMutated }) => {
  const { data: accounts, loading, error, refetch } = useCaseAccounts(kase.id);

  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<CaseAccount | null>(null);
  const [busy, setBusy] = useState(false);

  const linkedIds = new Set(accounts.map((a) => a.accountId));

  const loadAccountOptions = useCallback(async (term: string) => {
    try {
      const res = await accountCasesService.getActionable({ searchTerm: term || undefined, pageSize: 20 });
      return (res.data ?? [])
        .filter((a) => !linkedIds.has(String(a.accountId)))
        .map((a) => ({ value: String(a.accountId), label: `${a.accountNumber} — ${a.customerName ?? 'Unknown'}` }));
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const handleAdd = useCallback(async () => {
    if (!selectedAccountId) return;
    setAdding(true);
    try {
      await caseAccountService.create({
        caseId: kase.id, accountId: selectedAccountId,
        roleInCase: 'ADDITIONAL_CLAIM', isPrimary: false,
      });
      notificationService.toast.success('Account clubbed into the litigation.');
      setSelectedAccountId('');
      await refetch(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to add account.', 6000);
    } finally {
      setAdding(false);
    }
  }, [selectedAccountId, kase.id, refetch, onMutated]);

  const handleMarkPrimary = useCallback(async (a: CaseAccount) => {
    setBusy(true);
    try {
      await caseAccountService.markPrimary(a.id);
      notificationService.toast.success(`${a.accountNumber} set as primary.`);
      await refetch(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to set primary.', 6000);
    } finally {
      setBusy(false);
    }
  }, [refetch, onMutated]);

  const handleRemove = useCallback(async () => {
    if (!removeTarget) return;
    setBusy(true);
    try {
      await caseAccountService.delete(removeTarget.id);
      notificationService.toast.success('Account removed from the litigation.');
      setRemoveTarget(null);
      await refetch(); onMutated();
    } catch (e: any) {
      notificationService.toast.error(e?.response?.data?.message ?? 'Failed to remove account.', 6000);
    } finally {
      setBusy(false);
    }
  }, [removeTarget, refetch, onMutated]);

  if (loading && accounts.length === 0) {
    return <div style={{ padding: '1.5rem', color: 'var(--text-secondary)' }}>Loading accounts…</div>;
  }
  if (error) {
    return <div style={{ padding: '1.5rem', color: '#dc2626' }}>Failed to load: {error.message}</div>;
  }

  return (
    <div style={{ padding: '1rem 1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.85rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem' }}>Accounts ({accounts.length})</h3>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
          Loan accounts of the borrower / guarantors clubbed into this litigation.
        </span>
      </div>

      {/* Add account row */}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.85rem' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SyutSelect
            label="Add an account"
            isAsync
            loadOptions={loadAccountOptions}
            defaultOptions
            cacheOptions={false}
            value={selectedAccountId}
            onChange={(v) => setSelectedAccountId((v as string) || '')}
            placeholder="Search account # / customer…"
            size="sm"
          />
        </div>
        <button
          type="button" className="btn btn-primary"
          style={{ padding: '0.375rem 0.85rem' }}
          disabled={!selectedAccountId || adding}
          onClick={() => void handleAdd()}
        >
          <IconSystem.add size={14} style={{ marginRight: '0.35rem' }} />
          {adding ? 'Adding…' : 'Add'}
        </button>
      </div>

      {accounts.length === 0 ? (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', border: '1px dashed var(--border-color)', borderRadius: 6 }}>
          No accounts linked yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {accounts.map((a) => (
            <div key={a.id} style={{
              border: '1px solid var(--border-color)', borderRadius: 8, padding: '0.6rem 0.75rem',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
              background: 'var(--bg-primary)',
            }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{a.accountNumber ?? a.accountId.slice(0, 8)}</span>
                  {a.isPrimary
                    ? <span style={badge('#1e40af', '#dbeafe')}>Primary</span>
                    : <span style={badge('var(--text-secondary)', 'var(--bg-secondary)')}>{ROLE_LABEL[a.roleInCase] ?? a.roleInCase}</span>}
                  {a.isSecured && <span style={badge('#115e59', '#ccfbf1')}>Secured</span>}
                </div>
                <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                  {a.customerName ?? '—'} · {a.productCode ?? '—'} · O/S {fmtAmt(a.outstandingTotal)} · DPD {a.dpd ?? '—'}
                </div>
              </div>
              {!a.isPrimary && (
                <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                  <button type="button" className="btn btn-secondary" style={rowBtn} disabled={busy} onClick={() => void handleMarkPrimary(a)} title="Set as primary account">
                    Set Primary
                  </button>
                  <button type="button" style={{ ...iconBtn, color: '#dc2626' }} disabled={busy} onClick={() => setRemoveTarget(a)} title="Remove from litigation">
                    <IconSystem.trash size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove Account"
        message={`Remove ${removeTarget?.accountNumber ?? 'this account'} from the litigation? This does not affect the account itself.`}
        confirmLabel="Remove" cancelLabel="Cancel" variant="danger"
        loading={busy} loadingMessage="Removing…"
        onConfirm={() => void handleRemove()}
        onCancel={() => setRemoveTarget(null)}
      />
    </div>
  );
};

const badge = (fg: string, bg: string): React.CSSProperties => ({
  fontSize: '0.66rem', fontWeight: 700, padding: '1px 7px', borderRadius: 999, color: fg, background: bg,
});
const rowBtn: React.CSSProperties = { padding: '0.25rem 0.6rem', fontSize: '0.76rem' };
const iconBtn: React.CSSProperties = { border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', padding: '0.3rem 0.4rem', borderRadius: 6, display: 'inline-flex', alignItems: 'center' };

export default CaseAccountsSection;
